"use client";

import { useState } from "react";
import type { ReactNode } from "react";
import { Button, ButtonLink } from "@/components/button";
import { UpgradeButton } from "@/components/upgrade-button";
import { HeroBackdrop } from "@/components/hero-backdrop";
import { MarketingCta } from "@/components/marketing-cta";
import { AI_ASSIST_ENABLED } from "@/config/features";
import { AVATAR_RAMPS } from "./avatarPalette";

/*
 * Pricing page view (approved modern-warm design). All interactive UI lives
 * here: the Monthly/Annual toggle, the FAQ accordion, and the checkout
 * initiator. Plan view-models, comparison rows, and testimonials are built
 * server-side in page.tsx and passed in as serializable props. Token-only
 * styling via ./pricing.css.
 */

/* -------------------------------- types --------------------------------- */

export interface PlanCardView {
  tier: string;
  name: string;
  isPaid: boolean;
  /** The "most popular" tier: gets the accent border + chip. At most one card.
   *  Optional so existing fixtures need no change; absent reads as not highlighted. */
  highlighted?: boolean;
  /** Display name of the tier directly below this one (Free for Lite, Lite for
   *  Pro, …). Drives the "Everything in <below>, plus the list above." footnote.
   *  Optional; absent falls back to "Free" for single-tier upgrade surfaces. */
  belowTierName?: string;
  description: string;
  monthlyCents: number | null;
  annualCents: number | null;
  currency: string;
  features: string[];
}

/** One comparison column header (a plan). `accent` marks the highlighted plan. */
export interface CompareColumn {
  name: string;
  accent: boolean;
}

/** One comparison row: `values[i]` aligns with the i-th CompareColumn. */
export interface CompareRow {
  label: string;
  values: string[];
}

export interface CompareTable {
  columns: CompareColumn[];
  rows: CompareRow[];
}

export interface Testimonial {
  quote: string;
  name: string;
  role: string;
  company: string;
  /** Deep-link to the feature this quote is about (omitted when no page exists yet). */
  href?: string;
  linkLabel?: string;
}

/**
 * Generated testimonial avatar — a deterministic monogram, never a photo. Each
 * person gets a stable two-letter tile on a brand-palette gradient (hue chosen
 * from their name), ringed in white to echo the map markers. Honest by design:
 * it clearly represents an identity, it doesn't fabricate a face.
 */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

/** Stable non-negative hash of a string (djb2-ish) for deterministic hue pick. */
function nameHash(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return h;
}

function TestimonialAvatar({ name }: { name: string }) {
  const h = nameHash(name);
  const [from, to] = AVATAR_RAMPS[h % AVATAR_RAMPS.length]!;
  const angle = h % 360; // per-person gradient rotation for variety
  const uid = name.replace(/[^a-z0-9]/gi, "");
  const gid = `avg-${uid}`;
  const hid = `avh-${uid}`;
  return (
    <span className="pzp-quote__avatar" aria-hidden>
      <svg viewBox="0 0 40 40" width="34" height="34" role="presentation">
        <defs>
          <linearGradient id={gid} gradientTransform={`rotate(${angle} 0.5 0.5)`}>
            <stop offset="0" stopColor={from} />
            <stop offset="1" stopColor={to} />
          </linearGradient>
          {/* soft top-left sheen for depth — no hard shapes */}
          <radialGradient id={hid} cx="0.32" cy="0.26" r="0.8">
            <stop offset="0" stopColor="var(--color-surface-raised)" stopOpacity="0.38" />
            <stop offset="0.62" stopColor="var(--color-surface-raised)" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="20" cy="20" r="20" fill={`url(#${gid})`} />
        <circle cx="20" cy="20" r="20" fill={`url(#${hid})`} />
        <text x="20" y="20" textAnchor="middle" dominantBaseline="central">
          {initialsOf(name)}
        </text>
      </svg>
    </span>
  );
}

export interface LegalLinks {
  tos: string;
  privacy: string;
}

type Cycle = "monthly" | "annual";

/* ----------------------------- static copy ------------------------------ */

type StripIconName = "pin" | "link" | "download" | "spark";

const EVERY_PLAN: { t: string; icon: StripIconName }[] = [
  { t: "Keyless geocoding", icon: "pin" },
  { t: "Share + embed", icon: "link" },
  { t: "Export in 4 formats", icon: "download" },
  // AI/NL "Natural-language editing" hidden for launch (AI_ASSIST_ENABLED, MAP-135).
  ...(AI_ASSIST_ENABLED
    ? [{ t: "Natural-language editing", icon: "spark" } as { t: string; icon: StripIconName }]
    : []),
];

type WhyIconName = "bolt" | "spark" | "refresh" | "embed" | "api" | "lock";

function buildWhyCards(brandName: string): { t: string; d: string; icon: WhyIconName }[] {
  return [
    {
      t: "Paste to map in seconds",
      d: "Drop a CSV or paste straight from a spreadsheet. Addresses become pins before your coffee cools.",
      icon: "bolt",
    },
    // AI/NL "Edit with plain English" why-card hidden for launch (AI_ASSIST_ENABLED, MAP-135).
    ...(AI_ASSIST_ENABLED
      ? [
          {
            t: "Edit with plain English",
            d: "Type “color by territory” or “hide closed locations” into the NL bar and the map just does it.",
            icon: "spark",
          } as { t: string; d: string; icon: WhyIconName },
        ]
      : []),
    {
      t: "Live maps from a Google Sheet",
      d: "Connect a Sheet once and the map keeps itself current. Edit the rows and the pins follow.",
      icon: "refresh",
    },
    {
      t: "Embed anywhere",
      d: "One iframe snippet drops a full interactive map into your site, wiki, or client portal.",
      icon: "embed",
    },
    {
      t: "Public API",
      d: `Create and update maps programmatically. Wire ${brandName} into your own pipelines and tools.`,
      icon: "api",
    },
    {
      t: "Private & keyless by default",
      d: "No API keys to provision, and your maps stay unlisted until you decide otherwise.",
      icon: "lock",
    },
  ];
}

const FAQS: { q: string; a: string }[] = [
  {
    q: "Is there a free plan?",
    a: "Yes. One saved map, with share, embed, and export all included. And you don’t need to sign up to try it: make your first map first, keep it with an account.",
  },
  {
    q: "What happens if I cancel?",
    a: "You keep full access to your paid plan until the end of your current billing period. Prior charges are not refunded.",
  },
  {
    q: "What if I downgrade?",
    a: "Maps beyond the free limit become read-only until you resubscribe. They’re never deleted, and everything comes back exactly as you left it.",
  },
  {
    q: "What’s the difference between a saved map and a live map?",
    a: "A live map auto-refreshes from a connected source like a Google Sheet, so it stays current on its own. The free plan includes saved maps only, and you refresh them manually.",
  },
  {
    q: "Do I need a credit card to start?",
    a: "No. Make your first map without a card. You only add payment details if you upgrade to a paid plan.",
  },
];

/* -------------------------------- icons --------------------------------- */

const STROKES = {
  check: "M4 12l5 5 11-11",
  chevron: "m6 9 6 6 6-6",
  link: "M10.5 15.5l5-5M8 18a4.2 4.2 0 010-6l2.5-2.5M18 8a4.2 4.2 0 010 6l-2.5 2.5",
  download: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3",
  lock: "M7 11V7a5 5 0 0 1 10 0v4M5 11h14v10H5z",
  bolt: "M13 2L4.5 13.5H11L9.5 22 19 10h-6.5L13 2z",
  refresh: "M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6",
  embed: "M8 6l-6 6 6 6M16 6l6 6-6 6",
  api: "M4 17V7l8-4 8 4v10l-8 4-8-4zM4 7l8 4 8-4M12 11v10",
} as const;

function Stroke({ d, size = 16, color = "var(--color-accent-hover)" }: { d: string; size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden className="pzp-ico">
      <path d={d} style={{ stroke: color }} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Classic teardrop pin. Exported so the account Billing tab reuses the exact
 *  same pin as the pricing cards (MAP-163) rather than a look-alike. */
export function PinIcon({ fill, size = 14, rim }: { fill: string; size?: number; rim?: string }) {
  return (
    <svg width={size} height={size * 1.29} viewBox="0 0 34 44" fill="none" aria-hidden className="pzp-ico pzp-ico--block">
      <path
        d="M17 42.5C17 42.5 3.5 24.8 3.5 15.2C3.5 7.7 9.5 1.8 17 1.8C24.5 1.8 30.5 7.7 30.5 15.2C30.5 24.8 17 42.5 17 42.5Z"
        style={{ fill, stroke: rim }}
        strokeWidth={rim ? 1.6 : 0}
      />
      <circle cx="17" cy="15.2" r="5" style={{ fill: "var(--color-accent-on-accent)" }} />
    </svg>
  );
}

function SparkIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden className="pzp-ico">
      <path
        d="M10 1l1.9 6.1L18 9l-6.1 1.9L10 17l-1.9-6.1L2 9l6.1-1.9L10 1z"
        style={{ fill: "var(--color-accent-hover)" }}
      />
    </svg>
  );
}

function StripIcon({ name }: { name: StripIconName }) {
  if (name === "pin") return <PinIcon fill="var(--color-accent-default)" size={13} />;
  if (name === "spark") return <SparkIcon size={16} />;
  return <Stroke d={STROKES[name]} size={16} />;
}

function WhyIcon({ name }: { name: WhyIconName }) {
  if (name === "spark") return <SparkIcon size={22} />;
  if (name === "bolt") {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden className="pzp-ico">
        <path
          d={STROKES.bolt}
          style={{ fill: "var(--color-accent-hover)", stroke: "var(--color-accent-hover)" }}
          strokeWidth="1"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return <Stroke d={STROKES[name]} size={22} />;
}

/** Feature-list check bullet. */
function CheckItem({ children, dim = false }: { children: ReactNode; dim?: boolean }) {
  return (
    <li className={dim ? "pzp-check pzp-check--dim" : "pzp-check"}>
      <span aria-hidden className="pzp-check__dot">
        <Stroke d={STROKES.check} size={10} />
      </span>
      <span>{children}</span>
    </li>
  );
}

/**
 * Static "Contact us" Enterprise card (MAP-175) — not a real tier, so it isn't
 * built from a `PlanCardView`. Shared by /pricing and the account Billing tab
 * (MAP-163) so every plan grid that lists Pro Plus also offers Enterprise, one
 * component instead of a look-alike per surface.
 */
export function EnterpriseCard({
  contactHref,
  hideFeatures,
}: {
  contactHref: string;
  /** See `PlanCard`'s `hideFeatures` — same compact treatment for the Billing
   *  tab's switch-plan row. */
  hideFeatures?: boolean;
}) {
  return (
    <article className="pzp-card pzp-card--enterprise">
      <div className="pzp-card__head">
        <span aria-hidden className="pzp-card__headpin">
          <PinIcon fill="var(--color-accent-default)" size={16} />
        </span>
        <span className="pzp-card__name">Enterprise</span>
      </div>
      <p className="pzp-card__desc">For teams with massive data sets and needs beyond our Pro Plus plan.</p>
      <div className="pzp-card__pricerow">
        <span className="pzp-card__amount pzp-card__amount--custom">Custom</span>
      </div>
      {hideFeatures ? (
        <div className="pzp-card__spacer" aria-hidden />
      ) : (
        <ul className="pzp-card__features">
          <CheckItem>Everything in Pro Plus</CheckItem>
          <CheckItem>Higher &amp; custom limits</CheckItem>
          <CheckItem>SSO, SLA &amp; security review</CheckItem>
          <CheckItem>Dedicated support</CheckItem>
        </ul>
      )}
      <div className="pzp-card__ctawrap">
        <ButtonLink href={contactHref} variant="secondary" size="lg" fullWidth shape="rect">
          Contact us
        </ButtonLink>
      </div>
      <div className="pzp-card__foot">We&rsquo;ll tailor a plan to your data.</div>
    </article>
  );
}

/* -------------------------------- money --------------------------------- */

function formatCents(cents: number, currency: string): string {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(cents / 100);
}

/** Percent saved on annual vs 12x monthly, from the real plan numbers. */
function savePct(plan: PlanCardView): number | null {
  if (plan.monthlyCents === null || plan.annualCents === null) return null;
  const yearlyIfMonthly = plan.monthlyCents * 12;
  if (plan.annualCents >= yearlyIfMonthly) return null;
  return Math.round((1 - plan.annualCents / yearlyIfMonthly) * 100);
}

/**
 * The Monthly/Annual segmented toggle + its save-percent note, shared by
 * /pricing and the account Billing tab (review 2026-07-21) so the two pieces
 * of duplicated toggle markup can't drift again. The save note sits beside the
 * pill as plain text ("Save up to X% with annual"), not inside it — a colored
 * badge crammed into the "Annual" segment read as clutter.
 */
export function BillingPeriodToggle({
  annual,
  onChange,
  pct,
}: {
  annual: boolean;
  onChange: (annual: boolean) => void;
  pct: number | null;
}) {
  return (
    <div className="pzp-toggle-row">
      <div className="pzp-toggle" role="group" aria-label="Billing period">
        <button
          type="button"
          className={annual ? "pzp-toggle__seg" : "pzp-toggle__seg pzp-toggle__seg--active"}
          aria-pressed={!annual}
          onClick={() => onChange(false)}
        >
          Monthly
        </button>
        <button
          type="button"
          className={annual ? "pzp-toggle__seg pzp-toggle__seg--active" : "pzp-toggle__seg"}
          aria-pressed={annual}
          onClick={() => onChange(true)}
        >
          Annual
        </button>
      </div>
      {pct !== null ? <span className="pzp-toggle__savenote">Save up to {pct}% with annual</span> : null}
    </div>
  );
}

/* ------------------------------- plan card ------------------------------- */

/**
 * A single plan card. Reused by the pricing page AND the account Billing tab
 * (MAP-163): `currentTier` marks the signed-in user's current plan (its CTA
 * becomes a disabled "Your current plan"), and the optional checkout paths route
 * an in-account upgrade back to /account/billing. With neither set — the pricing
 * page — it renders exactly as before.
 */
export function PlanCard({
  plan,
  annual,
  currentTier,
  checkoutSuccessPath,
  checkoutCancelPath,
  hideFeatures,
}: {
  plan: PlanCardView;
  annual: boolean;
  currentTier?: string;
  checkoutSuccessPath?: string;
  checkoutCancelPath?: string;
  /** Drop the feature checklist (Billing tab's compact switch-plan cards): keeps
   *  name/price/one-line description/CTA only, so a row of cards stays short
   *  enough to fit on one line instead of scrolling. */
  hideFeatures?: boolean;
}) {
  const hasAnnual = plan.annualCents !== null;
  const cycle: Cycle = annual && hasAnnual ? "annual" : "monthly";
  const isCurrent = currentTier !== undefined && plan.tier === currentTier;
  // A card needs SOME distinguishing mark or it reads as identical to every
  // other plain card — "current plan" used to be button-text-only, so on the
  // Billing tab a merely-popular-but-not-current card (accent border + bold
  // chip) visually dominated the user's actual plan. Current always wins the
  // chip over "most popular": it's the more relevant fact to the viewer.
  const chipLabel = isCurrent ? "CURRENT PLAN" : plan.highlighted ? "MOST POPULAR" : null;
  const featured = isCurrent || plan.highlighted;

  let amount: string;
  if (plan.monthlyCents === null) {
    amount = formatCents(0, plan.currency);
  } else if (cycle === "annual" && plan.annualCents !== null) {
    amount = formatCents(Math.round(plan.annualCents / 12), plan.currency);
  } else {
    amount = formatCents(plan.monthlyCents, plan.currency);
  }

  return (
    <article className={featured ? "pzp-card pzp-card--featured" : "pzp-card"}>
      {chipLabel ? <span className="pzp-card__chip">{chipLabel}</span> : null}
      <div className="pzp-card__head">
        <span aria-hidden className="pzp-card__headpin">
          <PinIcon fill="var(--color-accent-default)" size={16} />
        </span>
        <span className="pzp-card__name">{plan.name}</span>
      </div>
      <p className="pzp-card__desc">{plan.description}</p>
      <div className="pzp-card__pricerow">
        <span className="pzp-card__amount">{amount}</span>
        <span className="pzp-card__per">{plan.isPaid ? "/mo" : "forever"}</span>
      </div>
      {hideFeatures ? (
        <div className="pzp-card__spacer" aria-hidden />
      ) : (
        <ul className="pzp-card__features">
          {plan.features.map((f) => (
            <CheckItem key={f} dim={!plan.isPaid}>
              {f}
            </CheckItem>
          ))}
        </ul>
      )}
      {isCurrent ? (
        <div className="pzp-card__ctawrap">
          <Button variant="secondary" size="lg" fullWidth shape="rect" disabled>
            Your current plan
          </Button>
        </div>
      ) : plan.isPaid ? (
        <div className="pzp-card__ctawrap">
          <UpgradeButton
            planId={plan.tier}
            cycle={cycle}
            successPath={checkoutSuccessPath}
            cancelPath={checkoutCancelPath}
            size="lg"
            shape="rect"
            fullWidth
          />
        </div>
      ) : (
        <div className="pzp-card__ctawrap">
          <ButtonLink href="/" variant="secondary" size="lg" fullWidth shape="rect">
            Get started
          </ButtonLink>
        </div>
      )}
      <div className="pzp-card__foot">
        {isCurrent
          ? "You’re on this plan."
          : plan.isPaid
            ? `Includes everything in ${plan.belowTierName ?? "Free"}.`
            : "No sign-up to try it."}
      </div>
    </article>
  );
}

/* ------------------------------ closing map ------------------------------ */

/* --------------------------------- page ---------------------------------- */

export function PricingView({
  plans,
  compare,
  testimonials,
  brandName,
  legal,
  enterpriseContactHref,
}: {
  plans: PlanCardView[];
  compare: CompareTable | null;
  testimonials: Testimonial[];
  brandName: string;
  legal: LegalLinks;
  /** When set, renders a static "Contact us" Enterprise card in the plan grid
   *  (not a real tier — no price/checkout). */
  enterpriseContactHref?: string;
}) {
  const [annual, setAnnual] = useState(true);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [showAllQuotes, setShowAllQuotes] = useState(false);
  const featuredQuoteCount = 6;

  // The highlighted paid card drives the annual toggle's save-% (any paid tier
  // with an annual price enables the toggle; today none do, so it stays hidden).
  const highlighted = plans.find((p) => p.highlighted) ?? plans.find((p) => p.isPaid) ?? null;
  const pct = highlighted ? savePct(highlighted) : null;
  const hasAnnual = plans.some((p) => p.isPaid && p.annualCents !== null);
  const whyCards = buildWhyCards(brandName);

  return (
    <main className="pzp">
      {/* ---------------------- compact header panel ----------------------- */}
      <section className="pzp-hero">
        <HeroBackdrop />
        <div className="pzp-hero__inner pzp-wrap">
          <h1 className="pzp-hero__title">Simple plans. Pick your scale.</h1>
          <p className="pzp-hero__lede">
            Free for the map you make once. Pro and Pro Plus for the maps that keep working after you close the tab.
          </p>
          {hasAnnual ? (
            <div className="pzp-hero__togglerow">
              <BillingPeriodToggle annual={annual} onChange={setAnnual} pct={pct} />
            </div>
          ) : null}
        </div>
      </section>

      {/* --------------------------- plan cards ---------------------------- */}
      <section className="pzp-plans pzp-wrap" aria-label="Plans">
        <div className="pzp-plans__grid">
          {plans.map((plan) => (
            <PlanCard key={plan.tier} plan={plan} annual={annual} />
          ))}
          {enterpriseContactHref ? <EnterpriseCard contactHref={enterpriseContactHref} /> : null}
        </div>
      </section>

      {/* ----------------------- in-every-plan strip ----------------------- */}
      <section className="pzp-strip pzp-wrap">
        <div className="pzp-strip__label">In every plan</div>
        <div className="pzp-strip__card">
          <div className="pzp-strip__grid">
            {EVERY_PLAN.map((f) => (
              <div key={f.t} className="pzp-strip__item">
                <span aria-hidden className="pzp-strip__icon">
                  <StripIcon name={f.icon} />
                </span>
                <span className="pzp-strip__text">{f.t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------ comparison table ------------------------- */}
      {compare ? (
        <section className="pzp-compare pzp-wrap">
          <h2 className="pzp-h2 pzp-h2--sm">Every difference, in one look.</h2>
          <p className="pzp-sectionlede pzp-sectionlede--narrow">
            No fine print elsewhere. This table is the whole deal.
          </p>
          <div className="pzp-compare__scroll">
            <table className="pzp-table">
              <thead>
                <tr>
                  <th scope="col" className="pzp-table__what">
                    What you get
                  </th>
                  {compare.columns.map((col) => (
                    <th
                      key={col.name}
                      scope="col"
                      className={col.accent ? "pzp-table__plan pzp-table__plan--accent" : "pzp-table__plan"}
                    >
                      {col.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {compare.rows.map((row) => (
                  <tr key={row.label}>
                    <td className="pzp-table__label">{row.label}</td>
                    {row.values.map((value, i) => {
                      const accent = compare.columns[i]?.accent ?? false;
                      const cls = [
                        "pzp-table__cell",
                        accent ? "pzp-table__cell--accent" : "",
                        value === "No" ? "pzp-table__cell--no" : "",
                      ]
                        .filter(Boolean)
                        .join(" ");
                      return (
                        <td key={compare.columns[i]?.name ?? i} className={cls}>
                          {value === "Yes" ? (
                            <span className="pzp-table__yes">
                              <Stroke d={STROKES.check} size={13} />
                              Yes
                            </span>
                          ) : (
                            value
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {/* ---------------------------- why grid ----------------------------- */}
      <section className="pzp-why pzp-wrap">
        <div className="pzp-why__band">
          <h2 className="pzp-h2">Why {brandName}</h2>
          <p className="pzp-sectionlede">The parts people mention when they tell a colleague about it.</p>
          <div className="pzp-why__grid">
            {whyCards.map((c) => (
              <div key={c.t} className="pzp-why__card">
                <span aria-hidden className="pzp-why__icon">
                  <WhyIcon name={c.icon} />
                </span>
                <h3 className="pzp-why__title">{c.t}</h3>
                <p className="pzp-why__desc">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------------- testimonials --------------------------- */}
      <section className="pzp-quotes pzp-wrap">
        <h2 className="pzp-h2">The map people actually share.</h2>
        <div className={`pzp-quotes__grid${showAllQuotes ? " pzp-quotes__grid--all" : ""}`}>
          {testimonials.map((t, i) => (
            <figure
              key={t.name}
              className={`pzp-quote${i >= featuredQuoteCount ? " pzp-quote--extra" : ""}`}
            >
              <svg width="26" height="20" viewBox="0 0 26 20" aria-hidden className="pzp-quote__mark">
                <path
                  d="M0 20V11.6C0 4.9 3.7 1 10.5 0l1.2 3.4C7.6 4.4 5.7 6.4 5.5 9.2H11V20H0zm15 0V11.6C15 4.9 18.7 1 25.5 0l1.2 3.4c-4.1 1-6 3-6.2 5.8H26V20H15z"
                  fill="currentColor"
                  fillOpacity="0.85"
                />
              </svg>
              <blockquote className="pzp-quote__text">{t.quote}</blockquote>
              {t.href && t.linkLabel && (
                <a className="pzp-quote__link" href={t.href}>
                  {t.linkLabel}
                  <span aria-hidden="true"> →</span>
                </a>
              )}
              <figcaption className="pzp-quote__attr">
                <TestimonialAvatar name={t.name} />
                <span>
                  <span className="pzp-quote__name">{t.name}</span>
                  <span className="pzp-quote__role">{t.role} · {t.company}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
        {testimonials.length > featuredQuoteCount && (
          <div className="pzp-quotes__more">
            <button
              type="button"
              className="pzp-quotes__more-btn"
              aria-expanded={showAllQuotes}
              onClick={() => setShowAllQuotes((v) => !v)}
            >
              {showAllQuotes ? "Show fewer" : `Show all ${testimonials.length} stories`}
            </button>
          </div>
        )}
      </section>

      {/* ------------------------------- faq ------------------------------- */}
      <section className="pzp-faqsec pzp-wrap">
        <h2 className="pzp-h2">Questions, answered plainly.</h2>
        <div className="pzp-faqsec__list">
          {FAQS.map((f, i) => {
            const open = openFaq === i;
            return (
              <div key={f.q} className={open ? "pzp-faq pzp-faq--open" : "pzp-faq"}>
                <button
                  type="button"
                  className="pzp-faq__btn"
                  onClick={() => setOpenFaq(open ? null : i)}
                  aria-expanded={open}
                >
                  <span className="pzp-faq__q">{f.q}</span>
                  <span aria-hidden className="pzp-faq__chev">
                    <Stroke
                      d={STROKES.chevron}
                      size={13}
                      color={open ? "var(--color-accent-on-accent)" : "var(--color-accent-hover)"}
                    />
                  </span>
                </button>
                {open ? <p className="pzp-faq__a">{f.a}</p> : null}
              </div>
            );
          })}
        </div>
      </section>

      {/* --------------------------- closing CTA --------------------------- */}
      <MarketingCta
        title="Make your first map free."
        lede="Drop in the spreadsheet you already have. Upgrade only when the map earns it."
        ctaLabel="Make my free map →"
        ctaHref="/"
        note="No sign-up · No card required · Your data stays yours"
      />

      {/* ----------------------------- terms ------------------------------- */}
      <footer className="pzp-terms pzp-wrap">
        <p className="pzp-terms__copy">
          Subscriptions renew automatically at the price shown until you cancel. You can cancel from your billing
          settings; access continues until the end of the current billing period, and prior charges are not refunded.
          If you downgrade, maps beyond the free limit become read-only until you resubscribe, rather than being
          removed.
        </p>
        <p className="pzp-terms__legal">
          <a href={legal.tos}>Terms</a>
          <span aria-hidden="true"> · </span>
          <a href={legal.privacy}>Privacy</a>
        </p>
      </footer>
    </main>
  );
}
